import { SectionCard } from '../yeni-design/primitives';
import { miktarMetni } from '../satis-analiz/bicim';
import { Bilgi } from '../satis-analiz/Bilgi';
import { BlokIskeleti } from '../satis-analiz/BlokIskeleti';
import { TiklaAc } from '../satis-analiz/TiklaAc';
import { oranMetni } from '../satis-analiz/bicim';
import type { Donem } from '../satis-analiz/donem';
import type { StokKaynaklari } from './ekranKaynaklari';
import { useStokSorgu, type StokKaynak, type StokSorguFiltresi } from './useStokVerisi';
import { kategoriyeTopla } from './toplama';
import { kategoriNormalize } from './kategori';
import { kapsamNotuGorunsunMu } from './kapsam';
import { fmtCarpan } from './bicimYardimcilari';
import { KartHatasi } from './KartHatasi';

interface Props {
  kaynaklar: StokKaynaklari;
  donem: Donem;
  sube?: string | null;
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 1 — Devir hızı (990048) — kategoriye Σdonem_cogs/Σort_envanter_deger
// ════════════════════════════════════════════════════════════════════════════════════════════

interface DevirSatiri {
  stok_kod: string;
  stok_ad: string;
  kategori: string;
  ort_envanter_deger: number;
  donem_cogs: number;
  devir_hizi: number;
  etkin_gun: number;
}

const DEVIR_BILGI = 'Dönemde satışı olmayan ürünler bu listede yoktur — onlar Ölü stok kartındadır.';

function DevirKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  const q = useStokSorgu<DevirSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-devir-iskelet" baslik="Devir hızı" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return <KartHatasi testid="akis-devir-hata" baslik="Devir hızı" yukseklik={200} mesaj="Devir hızı yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data.map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return null;

  // S6 — Σpay/Σpayda, satır oranlarının ortalaması DEĞİL (kategoriyeTopla zaten bunu garanti eder).
  const kategoriler = kategoriyeTopla(satirlar, 'donem_cogs', 'ort_envanter_deger')
    .filter((k): k is typeof k & { oran: number } => k.oran !== null)
    .sort((a, b) => b.oran - a.oran);
  if (kategoriler.length === 0) return null;

  return (
    <div data-testid="akis-devir" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Devir hızı"
        subtitle="Kategoriye göre — dönem COGS / ortalama envanter"
        actions={<Bilgi testid="akis-devir-bilgi" metin={DEVIR_BILGI} />}
      >
        <ul className="flex flex-col gap-1.5">
          {kategoriler.map((k) => (
            <li
              key={k.kategori}
              data-testid={`akis-devir-satir-${k.kategori}`}
              className="flex items-center justify-between gap-3 text-[13px]"
            >
              <span className="text-ink-900 truncate min-w-0">{k.kategori}</span>
              <span className="text-ink-700 tnum shrink-0">{fmtCarpan(k.oran)}</span>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 2 — Sell-through Oranı (990046) — kategoriye Σsatilan/Σalinan
// ════════════════════════════════════════════════════════════════════════════════════════════

interface SellThroughSatiri {
  stok_kod: string;
  stok_ad: string;
  kategori: string;
  alinan: number;
  satilan: number;
  erime_oran: number | null;
}

const SELLTHROUGH_BILGI = "%100'ün üstü, dönemde stok eritildiği (satılan > alınan) anlamına gelir.";

function SellThroughKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  const q = useStokSorgu<SellThroughSatiri>(kaynak, f);
  if (!kaynak) return null;
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-sellthrough-iskelet" baslik="Sell-through Oranı" yukseklik={200} satir={4} />;
  }
  if (q.isError) {
    return (
      <KartHatasi testid="akis-sellthrough-hata" baslik="Sell-through Oranı" yukseklik={200} mesaj="Sell-through oranı yüklenemedi." />
    );
  }
  if (q.data === undefined) return null;
  const satirlar = q.data.data.map((s) => ({ ...s, kategori: kategoriNormalize(s.kategori) }));
  if (satirlar.length === 0) return null;

  const kategoriler = kategoriyeTopla(satirlar, 'satilan', 'alinan')
    .filter((k): k is typeof k & { oran: number } => k.oran !== null)
    .sort((a, b) => b.oran - a.oran);
  if (kategoriler.length === 0) return null;

  return (
    <div data-testid="akis-sellthrough" className="h-full">
      <SectionCard
        className="h-full flex flex-col"
        bodyClass="p-5 flex-1 flex flex-col"
        title="Sell-through Oranı"
        subtitle="Kategoriye göre — dönemde satılan / alınan"
        actions={<Bilgi testid="akis-sellthrough-bilgi" metin={SELLTHROUGH_BILGI} />}
      >
        <ul className="flex flex-col gap-1.5">
          {kategoriler.map((k) => (
            <li
              key={k.kategori}
              data-testid={`akis-sellthrough-satir-${k.kategori}`}
              className="flex items-center justify-between gap-3 text-[13px]"
            >
              <span className="text-ink-900 truncate min-w-0">{k.kategori}</span>
              <span className="text-ink-700 tnum shrink-0">{oranMetni(k.oran * 100, 2)}</span>
            </li>
          ))}
        </ul>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Kart 3 — Stok Gün Sayısı (990047) — TiklaAc ile tembel, iki uçtan 10 ürün
// ════════════════════════════════════════════════════════════════════════════════════════════

interface StokGunSatiri {
  stok_kod: string;
  stok_ad: string;
  kategori: string;
  eldeki: number;
  gunluk_satis: number;
  kalan_gun: number | null;
}

const STOKGUN_BILGI =
  "Stok tarihçesi 2026-04-15'te başlar; daha eski dönem seçilirse gün sayısı olduğundan büyük çıkar.";
const STOKGUN_ADET = 10;

function StokGunTablo({
  testid, baslik, satirlar,
}: {
  testid: string;
  baslik: string;
  satirlar: { stok_kod: string; stok_ad: string; kalan_gun: number }[];
}) {
  return (
    <div className="flex-1 min-w-0">
      <h3 className="text-[12px] font-medium text-ink-500 mb-1.5">{baslik}</h3>
      <table data-testid={testid} className="w-full text-[12.5px] border-collapse">
        <thead>
          <tr className="text-ink-400 text-left">
            <th className="font-normal py-1">Ürün</th>
            <th className="font-normal py-1 text-right">Kalan gün</th>
          </tr>
        </thead>
        <tbody>
          {satirlar.map((s) => (
            <tr key={s.stok_kod} data-testid={`${testid}-${s.stok_kod}`} className="border-t border-border">
              <td className="py-1 pr-2 text-ink-800 truncate max-w-0 w-full">{s.stok_ad}</td>
              <td className="py-1 text-right tnum text-ink-700 whitespace-nowrap">{miktarMetni(s.kalan_gun)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * İki uçtan 10+10, ÇAKIŞMASIZ (SermayeBlok'un `enDusukEnYuksek`'iyle AYNI gerekçe ve AYNI kalıp:
 * ürün sayısı 2N'den azsa iki liste kesişmesin diye en fazla toplamın yarısı kadar alınır).
 *
 * Düzeltme turu 1/5, F3 — `toplam <= 1` durumu `enDusukEnYuksek` ile BİREBİR aynı çözülür: tek
 * (ya da hiç) geçerli ürün varsa "aşırı stok" karşılaştıracak ikinci ürün yok, boş kalır — tek ürün
 * hem "yakında bitecek" hem "aşırı stok" olarak İKİ KEZ görünmez. Önceki `Math.max(1, x) || toplam`
 * ifadesi ölü kodtu (`Math.max(1, x)` her zaman ≥1, yani her zaman truthy — `|| toplam` dalı asla
 * çalışmazdı) VE bu tekrarı ÖNLEMİYORDU (toplam=1'de her iki liste de aynı satırı üretiyordu).
 */
function ikiUctanOnAr(satirlar: StokGunSatiri[], n = STOKGUN_ADET) {
  const gecerli = satirlar.filter((s): s is StokGunSatiri & { kalan_gun: number } => s.kalan_gun !== null);
  const artan = [...gecerli].sort((a, b) => a.kalan_gun - b.kalan_gun);
  const toplam = artan.length;
  if (toplam <= 1) return { bitecek: artan, asiri: [] };
  const adet = Math.min(n, Math.floor(toplam / 2));
  const bitecek = artan.slice(0, adet);
  const asiri = artan.slice(toplam - adet).reverse();
  return { bitecek, asiri };
}

function StokGunIcerik({ kaynak, f }: { kaynak: StokKaynak; f: StokSorguFiltresi }) {
  const q = useStokSorgu<StokGunSatiri>(kaynak, f);
  if (q.isLoading) {
    return <BlokIskeleti testid="akis-stokgun-iskelet" yukseklik={220} satir={6} />;
  }
  if (q.isError) {
    return <KartHatasi testid="akis-stokgun-hata" yukseklik={220} mesaj="Stok gün sayısı yüklenemedi." />;
  }
  if (q.data === undefined) return null;
  const { bitecek, asiri } = ikiUctanOnAr(q.data.data);
  if (bitecek.length === 0 && asiri.length === 0) return null;

  return (
    <div className="p-5 flex flex-col sm:flex-row gap-5">
      <StokGunTablo testid="akis-stokgun-bitecek" baslik="Yakında bitecek" satirlar={bitecek} />
      <StokGunTablo testid="akis-stokgun-asiri" baslik="Aşırı stok" satirlar={asiri} />
    </div>
  );
}

function StokGunKarti({ kaynak, f }: { kaynak: StokKaynak | undefined; f: StokSorguFiltresi }) {
  if (!kaynak) return null;
  return (
    <div data-testid="akis-stokgun" className="h-full">
      <SectionCard className="h-full flex flex-col" bodyClass="p-0 flex-1 flex flex-col">
        <TiklaAc
          anahtar="akis-stokgun"
          baslik="Stok gün sayısı"
          altBaslik="İki uçtan 10 ürün — yakında bitecek / aşırı stok"
          bilgi={<Bilgi testid="akis-stokgun-bilgi" metin={STOKGUN_BILGI} />}
        >
          <StokGunIcerik kaynak={kaynak} f={f} />
        </TiklaAc>
      </SectionCard>
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════════════════════════════
// Blok — AKIŞ (§4.4)
// ════════════════════════════════════════════════════════════════════════════════════════════

const KAPSAM_NOT_METNI = 'Şube süzgeci bu blokta geçerli değil — ölçüler tüm şubeleri kapsar.';

/**
 * F-5 (final-fix, 2026-09-16) — S1'in sabit `sube !== null` koşulu `kapsamNotuGorunsunMu` ile
 * DEĞİŞTİRİLDİ: not artık bu bloğun üç ds'inin (990046/990047/990048) GERÇEK `@LOKASYON` bildirimine
 * bakar (`kapsam.ts`, SermayeBlok'taki `sermaye-kapsam-not` ile AYNI mekanizma). Bugün hiçbiri
 * bildirmiyor, davranış AYNI kalır; ileride bir ds `@LOKASYON` kazanırsa not otomatik kaybolur.
 */
export function AkisBlok({ kaynaklar, donem, sube = null }: Props) {
  const f: StokSorguFiltresi = { donem, sube, kategori: null };

  const hicKaynakYok = !kaynaklar.devirHizi && !kaynaklar.sellThrough && !kaynaklar.stokGun;
  if (hicKaynakYok) return null;

  const kapsamNotuGorunur = kapsamNotuGorunsunMu(
    [kaynaklar.devirHizi, kaynaklar.sellThrough, kaynaklar.stokGun],
    '@LOKASYON',
    sube,
  );

  return (
    <section data-testid="akis-blok" className="flex flex-col gap-3">
      <header className="flex items-baseline justify-between gap-3">
        <h2 className="text-[16px] font-semibold text-ink-900">
          Akış
          {kapsamNotuGorunur && (
            <span data-testid="akis-kapsam-not" className="block text-[11px] font-normal text-ink-400 mt-0.5">
              {KAPSAM_NOT_METNI}
            </span>
          )}
        </h2>
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 items-stretch">
        <DevirKarti kaynak={kaynaklar.devirHizi} f={f} />
        <SellThroughKarti kaynak={kaynaklar.sellThrough} f={f} />
      </div>
      <StokGunKarti kaynak={kaynaklar.stokGun} f={f} />
    </section>
  );
}
